import React, { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import SubHeader from '../../layout/components/SubHeader';
import Searchbar from './subparts/Searchbar';
import ContactList from './subparts/ContactList';
import ConversationList from './subparts/ConversationList';
import ContactListSkeleton from '../../smsgateway/pages/messages/widgets/ContactListSkeleton';
import ConversationSkeleton from '../../smsgateway/pages/messages/widgets/ConversationSkeleton';
import { toast } from 'react-toastify';
import smsgatewayService from '../../../services/smsgateway.service';
import { useEyeconxStore } from '../../../store/eyeconxStore';
import '../../smsgateway/layout/SmsSkeleton.css';
import './Messages.css';

const getContactDetails = (chatMessages) => {
    if (!Array.isArray(chatMessages) || chatMessages.length === 0) {
        return null;
    }

    return chatMessages.find((msg) => msg?.customerFirstName || msg?.customerLastName) || chatMessages[0];
};

const normalizeCustomerDob = (dob) => {
    if (!dob) {
        return null;
    }

    if (Array.isArray(dob) && dob.length >= 3) {
        const [year, month, day] = dob;
        return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    }

    return dob;
};

const buildSendMessagePayload = ({
    clinicId,
    clinicSms,
    customer,
    message,
    contactMessage,
    isConfirmationRequired,
}) => {
    const payload = {
        clinicId,
        business: clinicSms,
        customer,
        message,
        isConfirmationRequired,
    };

    if (contactMessage?.customerFirstName) {
        payload.customerFirstName = contactMessage.customerFirstName;
    }
    if (contactMessage?.customerLastName) {
        payload.customerLastName = contactMessage.customerLastName;
    }

    const customerDob = normalizeCustomerDob(contactMessage?.customerDob);
    if (customerDob) {
        payload.customerDob = customerDob;
    }

    return payload;
};

function Messages() {
    const location = useLocation();
    const pendingCustomer = useRef(location.state?.customer);
    const [searchTerm, setSearchTerm] = useState('');
    const [searchCriteria, setSearchCriteria] = useState('firstName');
    const [sortOrder, setSortOrder] = useState(location.state?.initialSort || 'desc');
    const [activeSearchString, setActiveSearchString] = useState('');
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [nextCursor, setNextCursor] = useState(null);
    const [hasNext, setHasNext] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    const [chat, setChat] = useState([]);
    const [loadConversation, setLoadConversation] = useState(false);
    const [conversationDetails, setConversationDetails] = useState([]);
    const [conversationDetailsLoading, setConversationDetailsLoading] = useState(false);
    const isMounted = useRef(false);
    const clinicId = useEyeconxStore((state) => state.user.clinicId);
    const clinicInfo = useEyeconxStore((state) => state.clinicInfo);

    useEffect(() => {
        if (!isMounted.current) {
            isMounted.current = true;
            getUserList();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (pendingCustomer.current) {
            handleUserSelect(pendingCustomer.current);
            pendingCustomer.current = null;
            window.history.replaceState({}, document.title);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleRefresh = () => {
        setSelectedUser(null);
        setConversationDetails(null);
        setUsers([]);
        setNextCursor(null);
        setHasNext(false);
        getUserList(null, 10, activeSearchString, sortOrder);
    };

    const handleDeleteConversation = () => {
        if (!selectedUser) return;

        smsgatewayService.deleteConversation(clinicId, selectedUser)
            .then(() => {
                toast.success('Conversation deleted successfully');
                handleRefresh();
            })
            .catch(() => {
                toast.error('Error deleting conversation');
            });
    };

    const getUserList = (cursor = null, size = 10, searchString = activeSearchString, sort = sortOrder) => {
        if (cursor === null) {
            setLoading(true);
        } else {
            setLoadingMore(true);
        }

        smsgatewayService.getInbox(clinicId, searchCriteria, searchString, cursor, size, sort)
            .then((res) => {
                if (cursor === null) {
                    setUsers(res.data.data);
                } else {
                    setUsers((prevUser) => [...prevUser, ...res.data.data]);
                }
                setNextCursor(res.data.nextCursor);
                setHasNext(res.data.hasNext);
                setLoading(false);
                setLoadingMore(false);
            })
            .catch(() => {
                toast.error('Error getting Users');
                setLoading(false);
                setLoadingMore(false);
            });
    };

    const handleSearchChange = (strSearch) => {
        setActiveSearchString(strSearch);
        setUsers([]);
        setNextCursor(null);
        setHasNext(false);
        getUserList(null, 10, strSearch, sortOrder);
    };

    const handleSortChange = (newSort, searchString = activeSearchString) => {
        setSortOrder(newSort);
        setActiveSearchString(searchString);
        setUsers([]);
        setNextCursor(null);
        setHasNext(false);
        getUserList(null, 10, searchString, newSort);
    };

    const handleSearchCriteriaChange = (e) => {
        setSearchCriteria(e.target.value);
    };

    const handleReset = () => {
        setSearchCriteria('firstName');
        setSearchTerm('');
        setActiveSearchString('');
        setUsers([]);
        setNextCursor(null);
        setHasNext(false);
        getUserList(null, 10, '', sortOrder);
    };

    const handleUserSelect = (user) => {
        handleConversation(user);
        setSelectedUser(user);
    };

    const handleLoadMore = () => {
        if (hasNext && !loadingMore) {
            getUserList(nextCursor, 10, activeSearchString, sortOrder);
        }
    };

    const handleConversation = (user, pageNo = 0, pageSize = 30) => {
        setLoadConversation(true);
        smsgatewayService.getConversation(clinicId, user, pageNo, pageSize)
            .then((res) => {
                setChat(res.data);
                setLoadConversation(false);
            })
            .catch(() => {
                toast.error('Error getting Conversation');
                setLoadConversation(false);
            });
    };

    const handleSendMessage = (message, isConfirmationRequired = false) => {
        if (message.trim() === '' || !selectedUser) return;

        const contactMessage = getContactDetails(chat);
        const customer = contactMessage?.customer || selectedUser;
        const sendNewMessage = buildSendMessagePayload({
            clinicId,
            clinicSms: clinicInfo.sms,
            customer,
            message,
            contactMessage,
            isConfirmationRequired,
        });

        smsgatewayService.sendMessage(clinicId, sendNewMessage)
            .then((res) => {
                const now = new Date();
                const formattedDate = now.toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric',
                });
                const formattedTime = now.toLocaleTimeString('en-US', {
                    hour: '2-digit',
                    minute: '2-digit',
                    hour12: true,
                });
                const modifiedAt = `${formattedDate} ${formattedTime}`;

                const newMessage = {
                    business: clinicInfo.sms,
                    customer: selectedUser,
                    message: res.data?.message ?? message,
                    customerFirstName: contactMessage?.customerFirstName || '',
                    customerLastName: contactMessage?.customerLastName || '',
                    customerDob: contactMessage?.customerDob || '',
                    messageType: 'OUTGOING',
                    status: 'ACCEPTED',
                    modifiedAt,
                };

                setChat((prevChat) => [...prevChat, newMessage]);
                setUsers([]);
                setNextCursor(null);
                setHasNext(false);
                getUserList(null, 10, activeSearchString, sortOrder);
            })
            .catch(() => {
                toast.error('Error sending Message');
            });
    };

    const getConversationDetails = () => {
        setConversationDetails(null);
        setConversationDetailsLoading(true);
        smsgatewayService.getConversationDetails(clinicId, selectedUser)
            .then((res) => {
                setConversationDetails(res.data);
                setConversationDetailsLoading(false);
            })
            .catch(() => {
                toast.error('Error getting Conversation Details');
                setConversationDetailsLoading(false);
            });
    };

    return (
        <div className="ecx-page messages-page">
            <SubHeader
                title="Messages"
                description="Communicate with patients using SMS"
                icon="bi-chat-left-text"
                containerClass="container-fluid px-4"
            >
                <button
                    className="btn btn-light-primary btn-sm rounded-pill"
                    onClick={handleRefresh}
                    title="Refresh messages"
                >
                    <i className="bi bi-arrow-clockwise me-1"></i>
                    Refresh
                </button>
            </SubHeader>
            <div className="container-fluid px-4 py-3">
                <div className="row g-3">
                    <div className="col-12 col-lg-4 col-xl-4">
                        <div className="card shadow-sm border-0" style={{ height: 'calc(100vh - 200px)' }}>
                            <div className="card-body p-3 d-flex flex-column h-100" style={{ overflow: 'hidden' }}>
                                <Searchbar
                                    handleSearchChange={handleSearchChange}
                                    searchCriteria={searchCriteria}
                                    handleSearchCriteriaChange={handleSearchCriteriaChange}
                                    searchTerm={searchTerm}
                                    handleReset={handleReset}
                                    sortOrder={sortOrder}
                                    handleSortChange={handleSortChange}
                                />
                                {loading ? (
                                    <ContactListSkeleton />
                                ) : (
                                    <ContactList
                                        users={users}
                                        handleUserSelect={handleUserSelect}
                                        selectedUser={selectedUser}
                                        handleLoadMore={handleLoadMore}
                                        hasNext={hasNext}
                                        loadingMore={loadingMore}
                                    />
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="col-12 col-lg-8 col-xl-8">
                        {selectedUser ? (
                            <>
                                {loadConversation ? (
                                    <ConversationSkeleton />
                                ) : (
                                    Array.isArray(chat) && chat.length > 0 && (
                                        <ConversationList
                                            smsData={chat}
                                            handleSendMessage={handleSendMessage}
                                            getConversationDetails={getConversationDetails}
                                            conversationDetails={conversationDetails}
                                            conversationDetailsLoading={conversationDetailsLoading}
                                            handleDeleteConversation={handleDeleteConversation}
                                        />
                                    )
                                )}
                            </>
                        ) : (
                            <div className="card shadow-sm border-0" style={{ height: 'calc(100vh - 200px)' }}>
                                <div className="card-body d-flex flex-column justify-content-center align-items-center text-center">
                                    <div className="bg-light rounded-circle p-4 mb-4">
                                        <i className="bi bi-chat-square-text text-primary" style={{ fontSize: '3rem' }}></i>
                                    </div>
                                    <h5 className="text-dark fw-semibold mb-2">Select a conversation</h5>
                                    <p className="text-muted mb-0">Choose a contact from the list to start messaging</p>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

export default Messages;
